import React, { RefObject } from "react";
import { ArrowUpIcon } from "@heroicons/react/24/outline";
import { useScrollToTop } from "../../hooks/useScrollToTop";
import { useHideScrollFabForGeniePanel } from "../../context/GeniePanelContext";

export interface ScrollToTopProps {
  /** Ref to the scrollable container (e.g. main content or a scrollable div). */
  scrollContainerRef: RefObject<HTMLElement | null>;
  /** Pixels scrolled before showing the button (default 300). */
  threshold?: number;
  /** Optional extra class for the button. */
  className?: string;
}

/** Stacked above Genie FAB: same right-6; bottom clears h-14 chat button + gap (Genie at bottom-[15px]). */
const defaultButtonClass =
  "fixed bottom-[calc(15px+3.5rem+0.5rem)] right-6 z-50 p-2 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 active:scale-95 border " +
  "bg-white hover:bg-gray-50 text-blue-600 hover:text-blue-700 border-gray-200 " +
  "dark:bg-slate-800 dark:hover:bg-slate-700 dark:text-sky-400 dark:hover:text-sky-300 dark:border-slate-600 dark:shadow-slate-950/50";

/**
 * Renders a floating "scroll to top" button when the user has scrolled past the threshold.
 * Use with a ref to your scrollable container so all modules get consistent scroll-to-top behavior.
 */
export const ScrollToTop: React.FC<ScrollToTopProps> = ({
  scrollContainerRef,
  threshold = 300,
  className = defaultButtonClass,
}) => {
  const { showButton, scrollToTop } = useScrollToTop(scrollContainerRef, threshold);
  const hideForGeniePanel = useHideScrollFabForGeniePanel();

  if (!showButton || hideForGeniePanel) return null;

  return (
    <button
      type="button"
      onClick={scrollToTop}
      className={className}
      aria-label="Scroll to top"
    >
      <ArrowUpIcon className="h-5 w-5 text-current" />
    </button>
  );
};
